import Accordion from 'LuxKit/Accordion'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import React from 'react'

export default function TravelPolicies() {
  return (
    <Group direction="vertical" gap={8}>
      <Heading variant="heading4">Travel policies</Heading>
      <Accordion size="large" heading="Why do I need a travel policy?">
        <BodyText variant="large">
          A travel policy is required because:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Cost Control:</BodyText>
              <BodyText variant="large"> A travel policy helps control expenses related to business travel by setting guidelines for permitted expenses, such as booking with preferred airlines or hotels. This ensures that employees adhere to budgetary constraints and avoid unnecessary expenses.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Risk Management:</BodyText>
              <BodyText variant="large"> By establishing guidelines for travel arrangements, accommodations and safety measures, a travel policy helps mitigate risks associated with business travel. This includes ensuring that employees stay in safe accommodations, use reputable transportation services and adhere to security protocols.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Compliance and Accountability:</BodyText>
              <BodyText variant="large"> A travel policy ensures that employees understand the company's expectations and requirements regarding business travel. It sets clear guidelines for booking procedures, expense reporting, reimbursement processes and adherence to regulatory requirements, promoting compliance and accountability among employees.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How do I create a travel policy for hotels?">
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access the Travel Policy Settings:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and navigate to the 'Travel Policy' tab. Within the settings menu, you'll find a dedicated section labelled 'Hotels'. Click on this tab to proceed.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Define Preferred Locations:</BodyText>
              <BodyText variant="large"> Within the Travel Policy page, you'll find a dedicated section specifically for hotels. Begin by specifying preferred locations where employees frequently travel or where accommodations meet your company's standards.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Set Per Night Pricing:</BodyText>
              <BodyText variant="large"> For each preferred location, set per night pricing based on your budget and the typical cost of accommodations in that area. This allows you to provide clear guidelines for employees when booking hotels within your specified locations.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Review and Save:</BodyText>
              <BodyText variant="large"> Once you've configured the hotel travel policy according to your preferences, take a moment to review the settings to ensure they align with your company's travel standards and objectives. Save the changes to activate the updated hotel travel policy.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How do I create a travel policy for flights?">
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Access the Travel Policy Settings:</BodyText>
              <BodyText variant="large"> Log in to your Business Traveller account and navigate to the 'Travel Policy' tab. Within the settings menu, you'll find a dedicated section labelled 'Flights'. Click on this tab to proceed.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Define Destination Routes and Prices:</BodyText>
              <BodyText variant="large"> Admins can set specific destination routes and establish pricing guidelines based on these routes. This allows you to control costs and ensure consistency in flight bookings.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Review and Save:</BodyText>
              <BodyText variant="large"> Once you've configured the flight travel policy according to your preferences, take a moment to review the settings to ensure they meet your requirements. Save the changes to activate the updated flight travel policy.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="How do I know what budgets I should be setting for accommodation and flights?">
        <BodyText variant="large">
          Determining suitable budgets for accommodation and flights requires a careful assessment of various factors, including your company's financial resources, travel frequency, employee preferences and travel destinations. Here's a guide to help you establish appropriate budgets:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Analyse Past Expenses:</BodyText>
              <BodyText variant="large"> Review past travel expenses to identify trends. Consider the average expenditures per trip, taking into account different destinations and travel durations.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Evaluate Travel Destinations:</BodyText>
              <BodyText variant="large"> Consider the destinations frequented by your employees and their associated costs.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Regular Review and Adjustment:</BodyText>
              <BodyText variant="large"> Periodically review and adjust budgets based on changes in market conditions, company growth or shifts in travel patterns. Flexibility is key to ensuring that budgets remain relevant.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
      <Accordion size="large" heading="What happens if I have a travel policy as well as an approval process in place?">
        <BodyText variant="large">
          Having both a travel policy and an approval process in place can improve control, compliance and efficiency in managing corporate travel. Here's how these two elements work together:
        </BodyText>
        <ol>
          <Group direction="vertical" gap={12}>
            <li>
              <BodyText variant="large" weight="bold">Travel Policy Compliance:</BodyText>
              <BodyText variant="large"> A travel policy outlines guidelines, rules and preferences for corporate travel, covering aspects such as booking procedures, budgetary limits and travel restrictions. When employees submit travel requests, they must adhere to the parameters set forth in the travel policy. This ensures consistency, cost-effectiveness and alignment with company objectives.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Approval Process Oversight:</BodyText>
              <BodyText variant="large"> An approval process involves routing travel requests through designated approvers or managers for review and authorisation before bookings are confirmed. Approvers assess the proposed travel against the established travel policy to ensure compliance and evaluate factors such as budget adherence and business necessity.</BodyText>
            </li>
            <li>
              <BodyText variant="large" weight="bold">Synergy Between Policy and Approval:</BodyText>
              <BodyText variant="large"> When a travel request is submitted, it must be verified to ensure compliance with the travel policy. The approval process serves as a checkpoint to validate that the proposed travel aligns with policy guidelines.</BodyText>
            </li>
          </Group>
        </ol>
      </Accordion>
    </Group>
  )
}
